/**
 * This component formats text across the application. It takes a key as
 * parameter that indentifies the string it has to use.
 *
 * This component can replace injected strings as %s characters, and templates
 * from moustache {{ }} with global variables from the CMS.
 *
 * The injected variables have to be passed as an array.
 *
 * This component also detect html, and use the HTMLSafeInjector component to
 * inject it safely.
 */

// @vendors
const isEmpty = require('lodash/lang/isEmpty');
const Mustache = require('mustache');

// @utilities
const storage = require('utilities/storage');
const { showKeys } = require('utilities/debugHelper');

// @constants
const { DEFAULT_TRANSLATION_TEXT, DEFAULT_TRANSLATION_TEXT_MISSING_VALUE, CC_TRANSLATION_PREFIX } = require('../constants/i18n');
const GLOBAL_VARIABLE_REFEX = /{{.+}}/;
const WARNINGS = true;


// Selected language
let language;
const getLanguage = (isContactCenter, defaulLanguage = "es") => {
    // Web stored language (persists)
    const cookieLanguage = storage.cookies.get('language');
    // CC stored language by session (persist)
    const localLanguage = localStorage.getItem('cclanguage');
    // Language to display. In CC the session language has more priority than the web language
    const storedLanguage = isContactCenter ? localLanguage : cookieLanguage;
    if (storedLanguage || defaulLanguage) {
        language = storedLanguage || defaulLanguage;
    }
    return language;
}

const isDefaultLanguage = () => {
    const splittedHost = window.location.hostname.split('.');
    const countryDomain = splittedHost[splittedHost.length - 1];
    return (countryDomain || '').toLowerCase() == (language || '').toLowerCase()
}

const reportTranslationError = (stringCode) => {
    if(window.ADRUM) {
        const errorT = new window.ADRUM.events.Error()
        errorT.msg(`missing translation: ${stringCode}`)
        window.ADRUM.report(errorT)
    }
}

// Gets the translated string from the stringcode
const getTranslation = (translations, stringCode, useLanguage = language, warnings = WARNINGS) => {
    // StringCode must exist and not be blank
    if (!stringCode || isEmpty(stringCode)) {
        if (warnings) window.console.log('Stringcode must exist and not be empty');
        return DEFAULT_TRANSLATION_TEXT;
    }

    // Context and term must exist
    const path = stringCode.split('-');
    const context = path[0];
    const term = path[1];
    if (!context || !term) {
        if (warnings) window.console.log(`The string code ${stringCode} should have a context and term`);
        return DEFAULT_TRANSLATION_TEXT;
    }

    // The translation should exist and not be blank
    let translation = translations.getIn(['translations', useLanguage, context, term], DEFAULT_TRANSLATION_TEXT);
    if (translation === null || translation === 'null') {
        translation = DEFAULT_TRANSLATION_TEXT_MISSING_VALUE;
    }
    if (translation === DEFAULT_TRANSLATION_TEXT || GLOBAL_VARIABLE_REFEX.test(stringCode)) {
        reportTranslationError(stringCode)
    }
    return translation;
}

// Checks if a string code has a translation (including cc translations)
const hasTranslation = (isContactCenter, translations, stringCode) => {
    let existsTranslation = false;

    // If CC we try to search for the cc string
    if (isContactCenter) {
        const ccStringCode = `${CC_TRANSLATION_PREFIX}${stringCode}`;
        const ccTranslation = getTranslation(translations, ccStringCode, language, false);
        existsTranslation = ccTranslation !== DEFAULT_TRANSLATION_TEXT;
    }

    // Get normal translation
    if (!existsTranslation) {
        const translation = getTranslation(translations, stringCode, language, false);
        existsTranslation = translation !== DEFAULT_TRANSLATION_TEXT;
    }

    return existsTranslation;
};

const stringCodeWithSuffix = (stringCode, originCountry) => {
    return `${stringCode}_${originCountry.toLowerCase()}`
}

const getTranslationSuffix = (translations, value, originCountry, stringCode, useLanguage) => {
    if (originCountry) {
        value = getTranslation(translations, stringCodeWithSuffix(stringCode, originCountry), useLanguage, WARNINGS);
    }
    if (value === DEFAULT_TRANSLATION_TEXT || value === DEFAULT_TRANSLATION_TEXT_MISSING_VALUE) {
        value = getTranslation(translations, stringCode, useLanguage, WARNINGS);
    }
    return value;
}

// Returns the translation of the string code (including cc translations)
const formatText = (isContactCenter, translations, isProduction, stringCode, injectedStrings, useLanguage = language, originCountry=undefined) => {
    let result = DEFAULT_TRANSLATION_TEXT;
    let finalStringCode = stringCode;

    // If CC we try to search for the cc string
    if (isContactCenter) {
        const ccStringCode = `${CC_TRANSLATION_PREFIX}${stringCode}`;
        finalStringCode = ccStringCode
        result = getTranslation(translations, ccStringCode, useLanguage, WARNINGS);
    }

    // Get normal translation
    if (result === DEFAULT_TRANSLATION_TEXT || result === DEFAULT_TRANSLATION_TEXT_MISSING_VALUE) {
        finalStringCode = originCountry ? stringCodeWithSuffix(stringCode, originCountry) : stringCode;
        result = getTranslationSuffix(translations, result, originCountry, stringCode, useLanguage);
    }

    if (showKeys(isProduction)) return finalStringCode;

    // If the translation is valid replace arguments
    if (result !== DEFAULT_TRANSLATION_TEXT && injectedStrings && injectedStrings instanceof Array) {
        injectedStrings.forEach(value => {
            result = result.replace(/%s/, value);
        });
    }

    return result;
};

const formatTextNamed = (isContactCenter, translations, isProduction, stringCode, immVariables, useLanguage = language, originCountry=undefined) => {
    let result = DEFAULT_TRANSLATION_TEXT;
    let finalStringCode = stringCode;

    // If CC we try to search for the cc string
    if (isContactCenter) {
        const ccStringCode = `${CC_TRANSLATION_PREFIX}${stringCode}`;
        finalStringCode = ccStringCode
        result = getTranslation(translations, ccStringCode, useLanguage, WARNINGS);
    }

    // Get normal translation
    if (result === DEFAULT_TRANSLATION_TEXT || result === DEFAULT_TRANSLATION_TEXT_MISSING_VALUE) {
        finalStringCode = originCountry ? stringCodeWithSuffix(stringCode, originCountry) : stringCode;
        result = getTranslationSuffix(translations, result, originCountry, stringCode, useLanguage);
    }

    if (showKeys(isProduction)) return finalStringCode;

    if (immVariables && immVariables.has('parameters')) {
        const globalVariables = immVariables.toJS();
        result = Mustache.render(result, globalVariables.parameters);
    }

    return result;
};

const formatTextApi = (isContactCenter, translations, isProduction, stringCode, stringValue, injectedStrings, useLanguage = language, originCountry=undefined) => {
    const newStringValue = stringValue.replace(/ /g, "_")
    const newStringCode =  `API${stringCode}${newStringValue}`;
    const translation = formatText(isContactCenter, translations, isProduction, newStringCode, injectedStrings, useLanguage, originCountry)
    if (translation !== DEFAULT_TRANSLATION_TEXT) {
        return translation
    } else if (useLanguage === 'es') {
        return stringValue
    } else {
        return translation
    }
}

const formatTextWithDefaultMessage = (isContactCenter, translations, isProduction, stringCode, injectedStrings, useLanguage = language, defaultMessageCode, defaultInjectedStrings, originCountry=undefined) => {
    const msg = formatText(isContactCenter, translations, stringCode, injectedStrings, useLanguage, originCountry);
    return msg !== DEFAULT_TRANSLATION_TEXT ? msg : formatText(isContactCenter, translations, isProduction, defaultMessageCode, defaultInjectedStrings, useLanguage, originCountry);
};

const localizationParser = {
    es: 'es-ES',
    en: 'en-ES',
    de: 'de-DE',
    pt: 'pt-PT',
    nl: 'nl-NL'
};

const getFormattedLanguage = (isContactCenter, language = getLanguage(isContactCenter)) => localizationParser[language] || 'es-ES';

module.exports.i18n = {
    getLanguage,
    isDefaultLanguage,
    hasTranslation,
    formatText,
    formatTextApi,
    formatTextWithDefaultMessage,
    getFormattedLanguage,
    formatTextNamed,
};
